import { useEffect, useState } from 'react';
import debounce from '../../../utils/debounce';
import { laneConfig } from '../config';
import { viewHoursMedium, viewHoursSmall, widthBoundaries } from '../config/view';

function getWindowDimensions(containerRef) {
  const { innerHeight: height } = window;
  // TODO Wait for containerRef to be loaded, otherwise everything will render at least twice.
  return {
    width: containerRef?.current?.clientWidth || document.body.clientWidth - 1,
    height,
  };
}

function getWeeks(workingDays, numberOfWeeks, isMonth) {
  const weeks = [];
  for (let weekNumber = 0; weekNumber < numberOfWeeks; weekNumber += 1) {
    if (isMonth && weekNumber === numberOfWeeks - 1) {
      weeks.push(workingDays.slice(0, 3));
    } else {
      weeks.push(workingDays);
    }
  }
  return weeks;
}

function getAmountDays(workingDays, numberOfWeeks, isMonth) {
  return isMonth ? 31 : workingDays.length * numberOfWeeks;
}

function calculateGanttDimensions(width, view = {}) {
  if (view?.parameters) {
    const {
      id,
      parameters: { startHour, endHour, workingDays, numberOfWeeks, widthDayBreaker },
    } = view;

    const amountOfDays = getAmountDays(workingDays, numberOfWeeks, id === 'month');

    // gantt width in percentage
    const widthGantt = width - laneConfig.widthFirstColumn;
    const widthFullDayBreaker = (amountOfDays - 1) * widthDayBreaker;
    const widthGanttAbsolute = ((widthGantt - widthFullDayBreaker) / widthGantt) * 100;

    // calculate width one day
    const widthDay = widthGanttAbsolute / amountOfDays;

    // calculate with dimension
    const amountOfHours = endHour - startHour;
    const widthHour = widthDay / amountOfHours;

    // In percentage
    const widthDimension = widthHour / 60;

    // In pixels
    // eslint-disable-next-line max-len
    const widthDimensionPx = (((widthGantt - widthFullDayBreaker) / amountOfDays) / amountOfHours) / 60;

    return {
      widthGantt,
      widthDay,
      widthDimension,
      widthDimensionPx,
    };
  }
  return {};
}

function calculateViewDimensions(width, view = {}) {
  if (view?.parameters) {
    const {
      id,
      type,
      calendarType,
      parameters: { startHour, endHour, workingDays, startOfWeek, numberOfWeeks },
    } = view;
    // get amount of hours to be shown per day
    let amountOfHours = endHour - startHour;
    const weeks = getWeeks(workingDays, numberOfWeeks, id === 'month');
    const amountOfDays = getAmountDays(workingDays, numberOfWeeks, id === 'month');

    let hoursArray = Array(amountOfHours)
      .fill()
      ?.map((value, index) => index + startHour);

    if (width < widthBoundaries.sm || viewHoursSmall.includes(id)) {
      amountOfHours = 2;
      hoursArray = hoursArray
        ?.filter(h => h === startHour || h === endHour - 1)
        ?.map(
          (e, i) => `${
            // eslint-disable-next-line
            i === 0 ? startHour : startHour + Math.floor((endHour - startHour) / 2) + 1
            }-${
            // eslint-disable-next-line
            i === 0 ? startHour + Math.floor((endHour - startHour) / 2) : endHour
            }`,
        );
    } else if (
      (width < widthBoundaries.lg && width >= widthBoundaries.sm) ||
      viewHoursMedium.includes(id)
    ) {
      amountOfHours /= 2;
      hoursArray = hoursArray?.filter(h => h % 2 !== 1);
    }
    // calculate width depth
    const widthHours = 100 / amountOfHours;
    const widthHour = 100 / (endHour - startHour);
    const widthDimension = widthHour / 60;

    return {
      type,
      id,
      calendarType,
      startHour,
      endHour,
      amountOfHours: hoursArray?.length,
      hoursArray,
      workingDays,
      amountOfDays,
      widthHour,
      widthHours,
      widthDimension,
      startOfWeek,
      numberOfWeeks,
      weeks,
    };
  }
  return {};
}

export default function useGanttEngine(containerRef) {
  const { width: initialWindowWidth, height: initialWindowHeight } = getWindowDimensions(
    containerRef,
  );
  const [ganttView, setGanttView] = useState(null);
  const [windowWidth, setWindowWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);
  const [ganttDimensions, setGanttDimensions] = useState({});
  const [viewDimensions, setViewDimensions] = useState({});

  const calculateGantt = () => {
    setGanttDimensions(calculateGanttDimensions(windowWidth, ganttView));
  };

  const calculateView = () => {
    setViewDimensions(calculateViewDimensions(windowWidth, ganttView));
  };

  useEffect(() => {
    if (initialWindowWidth) {
      setWindowWidth(initialWindowWidth);
    }
  }, [initialWindowWidth]);

  useEffect(() => {
    if (initialWindowHeight) {
      setWindowHeight(initialWindowHeight);
    }
  }, [initialWindowHeight]);

  useEffect(() => {
    function handleResize() {
      const { width, height } = getWindowDimensions(containerRef);
      setWindowWidth(width);
      setWindowHeight(height);
    }

    window.addEventListener(
      'resize',
      debounce(() => handleResize()),
    );
    return () =>
      window.removeEventListener(
        'resize',
        debounce(() => handleResize()),
      );
  }, []);

  useEffect(() => {
    if (ganttView && windowWidth && windowHeight) {
      calculateGantt();
      calculateView();
    }
  }, [ganttView?.id, windowWidth, windowHeight]);

  return {
    ganttDimensions,
    viewDimensions,
    updateView: setGanttView,
  };
}
